import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';
import type { UseUnsavedChangesWarningReturn } from '../../hooks/useUnsavedChangesWarning';

interface UnsavedChangesDialogProps {
  /** The whole return value of `useUnsavedChangesWarning` — pass it through. */
  guard: UseUnsavedChangesWarningReturn;
}

/**
 * The prompt every `useUnsavedChangesWarning` consumer owes, written once.
 *
 * The hook decides WHEN to ask (browser unload, Back, an in-app anchor click, a
 * guarded programmatic navigation); this is WHAT it asks. Rendering it is not
 * optional — a consumer that takes the hook and mounts no dialog blocks the
 * navigation and then never offers a way through, so the page simply stops
 * responding to its own sidebar.
 *
 * It exists as a component rather than a snippet because the alternative is seven
 * copies of the same four `t()` keys plus `destructive`, and a copy is exactly how
 * one surface ends up with a non-destructive confirm or a differently-worded
 * question. Where a form ALSO needs a second prompt for its own Cancel button —
 * which navigates programmatically, so the hook never sees it — that dialog is a
 * separate `<ConfirmDialog>` (reference: `EstimatorForm`), because its confirm
 * runs the caller's `onCancel`, not the hook's `confirmNavigation`.
 *
 * @example
 * const guard = useUnsavedChangesWarning({ isDirty });
 * // …
 * <UnsavedChangesDialog guard={guard} />
 */
export function UnsavedChangesDialog({ guard }: UnsavedChangesDialogProps) {
  const { t } = useTranslation('common');
  return (
    <ConfirmDialog
      open={guard.showBlockerDialog}
      onClose={guard.cancelNavigation}
      onConfirm={guard.confirmNavigation}
      title={t('app.unsavedChanges')}
      message={t('app.unsavedChangesMessage')}
      confirmLabel={t('app.leave')}
      cancelLabel={t('app.stay')}
      destructive
    />
  );
}
